// istanbul ignore file — composition shell; logic lives in hooks, slices, and child modules.

import { I18n } from '@calendly/i18n';
import { useCookieSettings } from '@calendly/onetrust';
import { BareButton } from '@calendly/ui/components/bare-button';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { AiSparkleBoldIcon, ArrowChevronRightMiniIcon } from '@calendly/ui/components/icons';
import { Group, Menu } from '@calendly/ui/components/menu';
import classNames from 'classnames';
import { useState, forwardRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useGetUserQuery } from 'common';
import { useHasSeenNotification, useUserSessionLogout } from 'features/admin';
import { useProductRefreshSettingsIa } from 'features/app_shell/product_refresh/useProductRefreshSettingsIa';
import { RequestToJoinModal, useFetchJoinableOrganizationsQuery } from 'features/organization';
import { DomainIcon } from 'features/personal';
import { confirmSettingsEntryNavigation } from 'features/settings_modal/confirm_settings_entry_navigation';
import { SETTINGS_SECTION } from 'features/settings_modal/constants';
import { useSettingsNavigation } from 'features/settings_modal/use_settings_navigation';
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events';
import { useTrack } from 'hooks/analytics_events/useTrack';
import { useNotetakerCoreUiVisible } from 'hooks/notetaker_visibility';
import { NOTETAKER_SETTINGS_PATH } from 'shared/constants/personal_browser_urls';
import { CustomBadge, MENU_ID, MENU_TOGGLE_ID, MenuContainer, MenuContent, MenuControl, MenuItem, UserAvatar } from './constants';
import { AppsIcon, CaretDownIcon, ExternalIcon, LinkIcon, LockIcon, LoginIcon, ProfileIcon, SettingsIcon, StarIcon } from './icons';
const menuHeaderCss = "munrfhl";
const MenuHeader = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(menuHeaderCss, props.className)} />;
});
const containerCss = "c1ycqnmq";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const joinOrganizationMenuItemCss = "j1vy80qq";
const JoinOrganizationMenuItem = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(joinOrganizationMenuItemCss, props.className)} />;
});
const joinOrganizationButtonCss = "j1k5d1o6";
const JoinOrganizationButton = forwardRef((props, ref) => <BareButton ref={ref} {...props} className={classNames(joinOrganizationButtonCss, props.className)} />);
export const OldDropDownUserMenu = ({
  ...pendoProps
}) => {
  const [joinAnOrgIsOpen, setJoinAnOrgIsOpen] = useState(false);
  const [toggleMenu, Dropdown, isExpanded] = useDropdown();
  const {
    isEnabled: isCookieSettingsEnabled,
    toggleBanner: toggleCookieSettings
  } = useCookieSettings();
  const {
    isSuccess,
    data
  } = useFetchJoinableOrganizationsQuery();
  const foundJoinableOrganization = isSuccess && data.collection.length > 0;
  const {
    data: {
      avatar_url: avatarUrl,
      avatar_letter: avatarLetter,
      is_super_admin: isSuperAdmin
    } = {},
    isLoading
  } = useGetUserQuery();
  const location = useLocation();
  const isRebrandSettingsIa = useProductRefreshSettingsIa();
  const {
    isVisible: isNotetakerCoreUiVisible
  } = useNotetakerCoreUiVisible();
  const [hasSeenNotification, setHasSeenNotification] = useHasSeenNotification();
  const track = useTrack();
  const {
    hrefForSettingsModalEntry
  } = useSettingsNavigation();
  const onClose = () => setJoinAnOrgIsOpen(false);
  const showJoinAnOrgModal = () => {
    setJoinAnOrgIsOpen(true);
    toggleMenu();
  };
  const setLocalStorageOnLogout = () => {
    window.localStorage.setItem('CALENDLY_AUTHENTICATED_USER_STATUS', 'false');
    if (hasSeenNotification) {
      setHasSeenNotification(false);
    }
  };
  const sendNavigationTrackEvent = navigationLinkId => track({
    name: TRACKING_EVENT_NAMES.NAVIGATION_LINK_CLICKED,
    properties: {
      navigation_location: 'profile_dropdown',
      navigation_link: navigationLinkId,
      source_url: location.pathname
    }
  });
  const handleSettingsEntryClick = (event, navigationLinkId) => {
    if (isRebrandSettingsIa && !confirmSettingsEntryNavigation(event)) {
      return;
    }
    sendNavigationTrackEvent(navigationLinkId);
  };
  const [logout] = useUserSessionLogout();
  const handleLogoutAction = () => {
    setLocalStorageOnLogout();
    logout();
  };
  if (isLoading) {
    return null;
  }
  const profileLinkTo = hrefForSettingsModalEntry(SETTINGS_SECTION.profile, '/app/personal/profile');
  return <Container>
      <MenuControl {...pendoProps} onClick={toggleMenu} aria-haspopup="true" aria-expanded={isExpanded} aria-controls={isExpanded ? MENU_ID : null} id={MENU_TOGGLE_ID} data-calendly-label="account-dropdown" aria-label={I18n.t('publisher.personal_settings.navigation.account_settings')}>
        <UserAvatar data-pendo-id="commerce-discount-code-enabled" src={avatarUrl} letter={avatarLetter} aria-hidden tabIndex={-1} size="small" expanded={isExpanded} />
        <CaretDownIcon />
      </MenuControl>
      <Dropdown>
        <MenuContainer>
          <Menu role="menu" id={MENU_ID} aria-labelledby={MENU_TOGGLE_ID}>
            <MenuContent>
              <Group>
                <MenuHeader>
                  {I18n.t('publisher.personal_settings.navigation.account_settings')}
                </MenuHeader>
              </Group>
              <Group>
                {foundJoinableOrganization && <JoinOrganizationMenuItem>
                    <JoinOrganizationButton onClick={showJoinAnOrgModal} role="menuitem">
                      {I18n.t('publisher.menu.join_an_org')}
                      <ArrowChevronRightMiniIcon />
                    </JoinOrganizationButton>
                  </JoinOrganizationMenuItem>}
                <MenuItem as={Link} onClick={event => handleSettingsEntryClick(event, 'profile')} to={profileLinkTo} role="menuitem" data-calendly-label="profile-link">
                  <ProfileIcon />
                  {I18n.t('publisher.personal_settings.navigation.profile')}
                </MenuItem>
                <MenuItem as={Link} onClick={() => sendNavigationTrackEvent('branding')} to={hrefForSettingsModalEntry(SETTINGS_SECTION.branding, '/app/personal/branding')} role="menuitem" data-calendly-label="branding-link">
                  <StarIcon />
                  {I18n.t('publisher.personal_settings.navigation.branding')}
                </MenuItem>
                <MenuItem as={Link} onClick={() => sendNavigationTrackEvent('my_link')} to={hrefForSettingsModalEntry(SETTINGS_SECTION.link, '/app/personal/link')} role="menuitem" data-calendly-label="my-link-link">
                  <LinkIcon />
                  {I18n.t('publisher.personal_settings.navigation.my_link')}
                </MenuItem>
                {isNotetakerCoreUiVisible && <MenuItem as={Link} to={hrefForSettingsModalEntry(SETTINGS_SECTION.ai_notetaker, NOTETAKER_SETTINGS_PATH)} role="menuitem" data-calendly-label="meeting-intelligence-link">
                    <AiSparkleBoldIcon />
                    {I18n.t('publisher.personal_settings.navigation.notetaker_settings')}
                  </MenuItem>}
                <MenuItem as={Link} onClick={() => sendNavigationTrackEvent('workflows')} to={hrefForSettingsModalEntry(SETTINGS_SECTION.workflows, '/app/personal/workflows')} role="menuitem" data-calendly-label="phone-number-link">
                  <DomainIcon />
                  {I18n.t('publisher.account_settings.communication_settings.title')}
                </MenuItem>
                <MenuItem as={Link} to={hrefForSettingsModalEntry(SETTINGS_SECTION.login_preferences, '/app/personal/login_preferences')} role="menuitem" data-calendly-label="login-preferences-link">
                  <LoginIcon />
                  {I18n.t('publisher.personal_settings.navigation.login_preferences')}
                </MenuItem>
                {isCookieSettingsEnabled && <MenuItem role="menuitem" onClick={toggleCookieSettings} data-calendly-label="cookie-settings-link">
                    <SettingsIcon />
                    {I18n.t('publisher.personal_settings.navigation.cookie_settings')}
                  </MenuItem>}
              </Group>
              <Group>
                <MenuItem as="a" target="_blank" onClick={() => sendNavigationTrackEvent('whats_new')} href="/release-notes" role="menuitem" rel="noreferrer noopener" data-calendly-label="whats-new-link">
                  <ExternalIcon />
                  {I18n.t('publisher.menu.new_dropdown.whats_new')}
                </MenuItem>
                <MenuItem as={Link} href="/app/apps" role="menuitem" data-calendly-label="apps-extensions-link">
                  <AppsIcon />
                  {I18n.t('publisher.menu.new_dropdown.apps_extensions')}
                </MenuItem>
                {isSuperAdmin && <MenuItem as="a" target="_blank" onClick={() => sendNavigationTrackEvent('super_admin')} href="/admin/dashboard" role="menuitem" rel="noreferrer noopener" data-calendly-label="super-admin-link">
                    <LockIcon />
                    {I18n.t('publisher.header.menu.admin_dashboard.label')}
                    <CustomBadge>{I18n.t('publisher.header.menu.admin_dashboard.badge')}</CustomBadge>
                  </MenuItem>}
              </Group>
              <Group>
                <MenuItem role="menuitem" onClick={handleLogoutAction} data-calendly-label="log-out-link">
                  {I18n.t('publisher.personal_settings.navigation.logout')}
                </MenuItem>
              </Group>
            </MenuContent>
          </Menu>
        </MenuContainer>
      </Dropdown>
      <RequestToJoinModal isOpen={joinAnOrgIsOpen} onClose={onClose} />
    </Container>;
};

require("./old-drop-down-user-menu.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./old-drop-down-user-menu.js");